<template>
    <div class="columns">
        <div class="column is-12">
            <div class="field is-grouped">
                <div class="control has-icons-left">
                    <input
                        class="input"
                        :class="searchQueryInputClass"
                        type="text"
                        :placeholder="translate('common.find')"
                        v-model="competitorQuery"
                    />
                    <span class="icon is-small is-left">
                        <i class="fa fa-search" />
                    </span>
                </div>
                <div class="control">
                    <div class="select">
                        <select v-model="limitCompetitorsNumber">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                        </select>
                    </div>
                </div>
            </div>

            <div class="field is-grouped">
                <div class="control">
                    <div class="checkbox">
                        <label class="has-text-info">
                            <input type="checkbox" v-model="showCandidates" />
                            {{ translate("admin_verification.candidates") }}
                        </label>
                    </div>
                </div>
                <div class="control">
                    <div class="checkbox">
                        <label class="has-text-success">
                            <input type="checkbox" v-model="showVerified" />
                            {{ translate("admin_verification.verified") }}
                        </label>
                    </div>
                </div>
                <div class="control">
                    <div class="checkbox">
                        <label class="has-text-warning">
                            <input type="checkbox" v-model="showDisqualified" />
                            {{ translate("admin_verification.disqualified") }}
                        </label>
                    </div>
                </div>
            </div>

            <table class="table is-hoverable is-fullwidth" v-if="competitors.length > 0 && requestsInProgress === 0">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{{ translate("common.first_name_and_last_name") }}</th>
                        <th>{{ translate("common.academy") }}</th>
                        <th>{{ translate("common.category") }}</th>
                        <th>{{ translate("common.status") }}</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(competitor, index) in filteredCompetitors" :key="competitor.id">
                        <td>{{ index + 1 }}</td>
                        <td class="clarity-mask">{{ competitor.firstName }} {{ competitor.lastName }}</td>
                        <td>{{ competitor.academy }} / {{ competitor.branch }}</td>
                        <td>{{ competitor.category }}</td>
                        <td>
                            <VerificationStatus :verification-state="competitor.status.value" />
                        </td>
                        <td>
                            <button class="button is-small" @click="openVerification(competitor)">
                                {{ translate("common.edit") }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="columns is-centered" v-else-if="requestsInProgress > 0">
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <div class="columns" v-if="requestsInProgress === 0">
                <div class="column is-6 is-offset-3">
                    <div
                        class="notification is-warning is-light has-text-centered"
                        v-if="filteredCompetitors.length === 0 || competitors.length === 0"
                    >
                        <span class="icon">
                            <i class="fa fa-search" />
                        </span>
                        <span>
                            {{ translate("admin_verification.no_results") }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <VerificationModal ref="verificationModal" @finished="refreshCompetitors" :event-types="eventTypes" />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import VerificationModal from "./VerificationModal.vue";
    import VerificationStatus from "./VerificationStatus.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import objectMatcher from "../Common/objectMatcher";
    import history from "../Common/history";
    import eventCompetitors, { CompetitorWithCategory } from "../Api/eventCompetitors";
    import { useVerificationStore } from "./verificationStore";

    const store = useVerificationStore();

    const historyInstance = history.getInstance(window);

    defineProps<{
        eventTypes: string[];
    }>();

    const verificationModal = ref<ComponentExposed<typeof VerificationModal> | null>(null);

    const limitCompetitorsNumber = ref(25);
    const competitorQuery = ref("");

    const showCandidates = ref(true);
    const showVerified = ref(false);
    const showDisqualified = ref(false);

    const competitors = ref<CompetitorWithCategory[]>([]);
    const requestsInProgress = ref(0);

    const filteredCompetitors = computed(() => {
        const filtered = competitors.value.filter(competitor => {
            if (!objectMatcher.matchBySearchQuery(competitor, competitorQuery.value)) {
                return false;
            }
            if (isVerified(competitor) && !showVerified.value) {
                return false;
            }
            if (isDisqualified(competitor) && !showDisqualified.value) {
                return false;
            }
            if (isCandidate(competitor) && !showCandidates.value) {
                return false;
            }

            return true;
        });

        return filtered.slice(0, limitCompetitorsNumber.value);
    });

    const searchQueryInputClass = computed(() => {
        if (competitorQuery.value.length > 0 && filteredCompetitors.value.length === 0) {
            return "is-danger";
        }
        return "";
    });

    watch(limitCompetitorsNumber, () => {
        historyInstance.setQueryParam("limit", limitCompetitorsNumber.value);
    });

    watch(showVerified, () => {
        historyInstance.setQueryParam("showVerified", showVerified.value);
    });

    watch(showDisqualified, () => {
        historyInstance.setQueryParam("showDisqualified", showDisqualified.value);
    });

    watch(competitorQuery, () => {
        historyInstance.setQueryParam("competitorQuery", competitorQuery.value);
    });

    onMounted(() => {
        initializeParametersFromLocation();

        return fetchCompetitors().catch(handleError);
    });

    function openVerification(competitor: CompetitorWithCategory) {
        verificationModal.value?.open(competitor);
    }

    function refreshCompetitors() {
        return fetchCompetitors().catch(handleError);
    }

    function fetchCompetitors() {
        requestsInProgress.value = 1;
        return eventCompetitors
            .getAllCompetitors(store.eventId)
            .then(resp => {
                competitors.value = resp;
            })
            .finally(() => {
                requestsInProgress.value = 0;
            });
    }

    function initializeParametersFromLocation() {
        const showVerifiedParam = historyInstance.getQueryParam("showVerified");
        showVerified.value = showVerifiedParam === "true";

        const showDisqualifiedParam = historyInstance.getQueryParam("showDisqualified");
        showDisqualified.value = showDisqualifiedParam === "true";

        competitorQuery.value = historyInstance.getQueryParam("competitorQuery") || "";

        const limitParam = historyInstance.getQueryParam("limit");
        limitCompetitorsNumber.value = limitParam ? parseInt(limitParam) : 25;
    }

    function isVerified(competitor: CompetitorWithCategory) {
        return competitor.status.value === 4;
    }

    function isDisqualified(competitor: CompetitorWithCategory) {
        return competitor.status.value === 2 || competitor.status.value === 3;
    }

    function isCandidate(competitor: CompetitorWithCategory) {
        return competitor.status.value === 0;
    }
</script>
